/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAlignment,
  selectBreakdown,
  selectDateComparison,
  selectSearchAlignmentToggle,
  selectSymmetricComparisonToggle,
  selectIsDetailed,
  selectSearchEndDate,
  selectSearchBookingDate,
  selectSearchStartDate,
  selectSearchVisualToggle,
  selectSearchFilterList,
  selectPaceVisualFilterList,
  selectEnableSecondaryHotelGroups,
  selectHotelFocusOn,
  selectNormalizedView,
  selectIsCumulative,
  selectFocusOn,
  selectSearchTimeEndDate,
  selectSearchTimeStartDate,
  selectIsMovingAverage,
  selectIsTrendDeAggregate,
  selectIsPercentageChange,
  selectTrendTimeWeeklyView,
  selectIsForecast,
  selectMovingAverage,
  selectGranularity,
  selectTrendTimeFilterList,
  selectTrendTimeTabularFilterList,
  selectAdjustedStartDate,
  selectAdjustedEndDate,
  selectSegmentGroup1,
  selectSegmentGroup2,
} from 'modules/dashboard/components/tab-container/search/selectors';
import {
  selectPaceHorizon,
  selectPaceVariable,
  selectTargetDate,
  selectVisualFilters,
  selectHotelName,
  selectPmsSync,
  selectEnableHotelGroups,
  selectSummaryForecastFilter,
  selectReportType,
  selectHotelGroupDropdownList,
  selectHotelDropdownList,
  selectIsPrimaryHotelSetOverTime,
  selectInvalidPrimaryHotelList,
  selectIsSecondaryHotelSetOverTime,
  selectInvalidSecondaryHotelList,
  selectResetBookingDate,
  selectIsDemoUser,
  selectWeeklyTrendStartDate,
  selectWeeklyTrendEndDate,
  selectPartialHours,
  selectLastPartialBookingDate,
} from 'modules/dashboard/selectors';
import { searchActions } from 'modules/dashboard/components/tab-container/search/slice';
import { KEYS, TAGS } from 'modules/dashboard/constants';
import {
  getAlignmentAndDateComparisonSchema,
  getBookingDateSchema,
  getChartBreakdownFilterList,
  getComparisonStayDateSchema,
  getComparisonBookingDateSchema,
  getDashboardPmsSyncSchema,
  getFarFieldPmsSyncSchema,
  getGroupASchema,
  getGroupHotelFilterSchema,
  getHotelGroupASchema,
  getHotelGroupSchema,
  getHotelIdSchema,
  getIsForecastSchema,
  getNormalizedViewSchema,
  getPaceHorizonSchema,
  getPaceVariableSchema,
  getPmsSyncSchema,
  getRevenueForecastSchema,
  getRoomForecastSchema,
  getSymmetricComparisonSchema,
  getTargetDateSchema,
  getMaxStayDateSchema,
  getHotelGroupOrderSchema,
  getGranularitySchema,
  getMovingAverageSchema,
  getSegmentFocusOnSchema,
  getTimeSegmentBreakdownAndFocusOnSchema,
  getPmsSyncToggleSchema,
  getTrendTimeAlignmentAndDateComparisonSchema,
  getTrendGroup1And2Schema,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import { getSelectedBreakdown } from 'modules/dashboard/functions';
import {
  endOfMonth,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameDay,
  isSaturday,
  isSunday,
  lastDayOfYear,
  nextSaturday,
  previousSunday,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import calculateComparisonDates, {
  hasLeapDay,
} from '../../../../../common/utils/dateComparisonUtils';
import { getGranularity, getTSBreakdownAndFocusOnSchema } from '../../trend-tabs/functions';
import { GRANULARITY } from '../../trend-tabs/constants';

/**
 * Custom hook to generate search widget filter schemas
 */
const useSearchFilter = () => {
  const dispatch = useDispatch();
  //
  const summaryForecastFilter = useSelector(selectSummaryForecastFilter);
  const chartBreakdown = useSelector(selectBreakdown);
  const alignment = useSelector(selectAlignment);
  const dateComparison = useSelector(selectDateComparison);
  const bookingDate = useSelector(selectSearchBookingDate);
  const targetDate = useSelector(selectTargetDate);
  const hotelId = useSelector(selectHotelName);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const paceHorizon = useSelector(selectPaceHorizon);
  const paceVariable = useSelector(selectPaceVariable);
  const symmetricComparisonToggle = useSelector(selectSymmetricComparisonToggle);
  const isDetailed = useSelector(selectIsDetailed);
  const endDate = useSelector(selectSearchEndDate);
  const startDate = useSelector(selectSearchStartDate);
  const alignmentToggle = useSelector(selectSearchAlignmentToggle);
  const visualToggle = useSelector(selectSearchVisualToggle);
  const reportType = useSelector(selectReportType);
  const searchFilterList = useSelector(selectSearchFilterList);
  const paceVisualFilterList = useSelector(selectPaceVisualFilterList);
  const hotelGroupsDropdownList = useSelector(selectHotelGroupDropdownList);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const hotelDropdownList = useSelector(selectHotelDropdownList);
  const normalizedViewToggle = useSelector(selectNormalizedView);
  const isPrimaryHotelSetOverTime = useSelector(selectIsPrimaryHotelSetOverTime);
  const invalidPrimaryHotels = useSelector(selectInvalidPrimaryHotelList);
  const isSecondaryHotelSetOverTime = useSelector(selectIsSecondaryHotelSetOverTime);
  const invalidSecondaryHotels = useSelector(selectInvalidSecondaryHotelList);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const targetStartDate = useSelector(selectWeeklyTrendStartDate);
  const targetEndDate = useSelector(selectWeeklyTrendEndDate);
  //
  const isCumulative = useSelector(selectIsCumulative);
  const startDateComparison = useSelector(selectSearchTimeStartDate);
  const endDateComparison = useSelector(selectSearchTimeEndDate);
  const focusOn = useSelector(selectFocusOn);
  const isTrendDeAggregate = useSelector(selectIsTrendDeAggregate);
  const isDemoUser = useSelector(selectIsDemoUser);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const isTrendPercentageChange = useSelector(selectIsPercentageChange);
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const isTrendForecast = useSelector(selectIsForecast);
  const movingAverage = useSelector(selectMovingAverage);
  const granularity = useSelector(selectGranularity);
  const trendTimeFilterList = useSelector(selectTrendTimeFilterList);
  const trendTabularFilterList = useSelector(selectTrendTimeTabularFilterList);
  const adjustedStartDate = useSelector(selectAdjustedStartDate);
  const adjustedEndDate = useSelector(selectAdjustedEndDate);
  const segmentGroup1 = useSelector(selectSegmentGroup1);
  const segmentGroup2 = useSelector(selectSegmentGroup2);
  const partialHours = useSelector(selectPartialHours);
  const lastPartialBookingDate = useSelector(selectLastPartialBookingDate);

  // Triggered when pmsSyncToggle,chartBreakdown,bookingDate,visualFilters,dateComparison,alignment,targetDate,hotelId,symmetricComparisonToggle,isDetailed,endDate,startDate value changes
  useEffect(() => {
    if (
      !chartBreakdown ||
      !alignment ||
      !bookingDate ||
      !targetDate ||
      !visualFilters ||
      !hotelId ||
      (dateComparison && dateComparison === KEYS.CUSTOM && endDate === null)
    )
      return;
    // Generate filter list for search widget top 8 visuals
    const targetDateFilter = getTargetDateSchema(
      visualFilters,
      targetDate?.startDate,
      targetDate?.endDate
    );
    //
    let hotelListIds = [];
    let hotelGroup1Filter;

    // Extract invalid primary IDs
    const invalidPrimaryIds = invalidPrimaryHotels.map((hotel) => hotel.hotelCode);

    if (hotelId?.hotels) {
      if (isPrimaryHotelSetOverTime) {
        hotelListIds = hotelId.hotels
          .filter((hotel) => !invalidPrimaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }

    // Extract invalid secondary IDs
    const invalidSecondaryIds = invalidSecondaryHotels.map((hotel) => hotel.hotelCode);

    let groupFocusOnIds = [];
    if (hotelFocusOn?.hotels) {
      if (isSecondaryHotelSetOverTime) {
        groupFocusOnIds = hotelFocusOn.hotels
          .filter((hotel) => !invalidSecondaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        groupFocusOnIds = hotelFocusOn?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }
    //
    if (enableHotelGroups) {
      hotelGroup1Filter = getHotelGroupSchema(hotelListIds);
    } else {
      hotelGroup1Filter = getHotelGroupSchema([hotelId?.hotelCode]);
    }
    //
    let hotelGroupAFilter;
    if (enableSecondaryHotelGroups) {
      hotelGroupAFilter = getHotelGroupASchema(groupFocusOnIds);
    } else {
      hotelGroupAFilter = getHotelGroupASchema([hotelFocusOn?.hotelCode]);
    }
    //
    let dimHotelIds = [];
    if (enableHotelGroups) {
      if (enableSecondaryHotelGroups) {
        dimHotelIds = new Set([...groupFocusOnIds, ...hotelListIds]);
      } else {
        dimHotelIds = new Set([hotelFocusOn?.hotelCode, ...hotelListIds]);
      }
    } else if (enableSecondaryHotelGroups) {
      dimHotelIds = new Set([...groupFocusOnIds, hotelId?.hotelCode]);
    } else {
      dimHotelIds = new Set([hotelFocusOn?.hotelCode, hotelId?.hotelCode]);
    }
    //
    const secondaryHotelIdFilter = getHotelIdSchema(Array.from(dimHotelIds), visualFilters);
    const hotelFilters = [secondaryHotelIdFilter, hotelGroupAFilter, hotelGroup1Filter];
    //
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    const alignmentAndDateComparisonFilter = getAlignmentAndDateComparisonSchema(
      visualFilters,
      alignment?.id,
      dateComparison
    );
    const symmetricComparisonFilter = getSymmetricComparisonSchema(
      visualFilters,
      symmetricComparisonToggle
    );
    const chartBreakdownFilter = getChartBreakdownFilterList(
      visualFilters,
      chartBreakdown,
      isDetailed
    );
    const pmsSyncFilter = getPmsSyncSchema(visualFilters, pmsSyncToggle, TAGS.SUMMARY);
    const normalizedFilter = getNormalizedViewSchema(visualFilters, normalizedViewToggle);
    const searchPmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    //
    const { adjustedBookingDate, adjustedStayDate } = calculateComparisonDates(
      targetDate,
      bookingDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      { startDate, endDate },
      resetBookingDate,
      partialHours,
      lastPartialBookingDate
    );
    const comparisonBookingDateFilter = getComparisonBookingDateSchema(
      visualFilters,
      adjustedBookingDate?.startDate,
      adjustedBookingDate?.endDate
    );
    const comparisonStayDateFilter = getComparisonStayDateSchema(
      visualFilters,
      adjustedStayDate?.startDate,
      adjustedStayDate?.endDate
    );
    const maxStayDateFilter = getMaxStayDateSchema(visualFilters, targetDate?.endDate);
    //
    const hasLeap = hasLeapDay(adjustedStayDate?.startDate, adjustedStayDate?.endDate);
    dispatch(searchActions.setIsSearchLeapComparison(hasLeap));
    //
    if (
      chartBreakdownFilter &&
      bookingDateFilter &&
      alignmentAndDateComparisonFilter &&
      targetDateFilter &&
      symmetricComparisonFilter &&
      normalizedFilter &&
      searchPmsSyncSchema
    ) {
      const filters = [
        ...searchPmsSyncSchema,
        ...pmsSyncFilter,
        chartBreakdownFilter,
        bookingDateFilter,
        ...alignmentAndDateComparisonFilter,
        targetDateFilter,
        symmetricComparisonFilter,
        ...hotelFilters,
        normalizedFilter,
        comparisonBookingDateFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];
      //
      if (JSON.stringify(filters) !== JSON.stringify(searchFilterList)) {
        dispatch(searchActions.updateSearchFilterList(filters));
      }
    } else dispatch(searchActions.updateSearchFilterList([]));
  }, [
    pmsSyncToggle,
    chartBreakdown,
    bookingDate,
    visualFilters,
    dateComparison,
    alignment,
    targetDate,
    hotelId,
    symmetricComparisonToggle,
    isDetailed,
    endDate,
    startDate,
    enableHotelGroups,
    reportType,
    enableSecondaryHotelGroups,
    hotelFocusOn,
    hotelGroupsDropdownList,
    normalizedViewToggle,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
    isSecondaryHotelSetOverTime,
    invalidSecondaryHotels,
    resetBookingDate,
  ]);

  // Triggered when pmsSyncToggle,visualFilters,dateComparison,alignment,targetDate,hotelId,paceHorizon,paceVariable,endDate,startDate value changes
  useEffect(() => {
    if (
      !dateComparison ||
      !alignment ||
      !targetDate ||
      !visualFilters ||
      !hotelId ||
      !paceHorizon ||
      !paceVariable ||
      (dateComparison && dateComparison === KEYS.CUSTOM && endDate === null)
    )
      return;
    // Generate filter list for search widget pace visual
    const targetDateFilter = getTargetDateSchema(
      visualFilters,
      targetDate?.startDate,
      targetDate?.endDate
    );
    //
    let hotelListIds = [];
    let hotelGroup1Filter;

    // Extract invalid primary IDs
    const invalidPrimaryIds = invalidPrimaryHotels.map((hotel) => hotel.hotelCode);

    if (hotelId?.hotels) {
      if (isPrimaryHotelSetOverTime) {
        hotelListIds = hotelId.hotels
          .filter((hotel) => !invalidPrimaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }

    // Extract invalid secondary IDs
    const invalidSecondaryIds = invalidSecondaryHotels.map((hotel) => hotel.hotelCode);

    let groupFocusOnIds = [];
    if (hotelFocusOn?.hotels) {
      if (isSecondaryHotelSetOverTime) {
        groupFocusOnIds = hotelFocusOn.hotels
          .filter((hotel) => !invalidSecondaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        groupFocusOnIds = hotelFocusOn?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }
    //
    const groupHotelFilter = getGroupHotelFilterSchema(visualFilters);
    if (enableHotelGroups) {
      hotelGroup1Filter = getHotelGroupSchema(hotelListIds, groupHotelFilter);
    } else {
      hotelGroup1Filter = getHotelGroupSchema([hotelId?.hotelCode], groupHotelFilter);
    }
    //
    const groupAFilter = getGroupASchema(visualFilters);
    let hotelGroupAFilter;
    if (enableSecondaryHotelGroups) {
      hotelGroupAFilter = getHotelGroupASchema(groupFocusOnIds, groupAFilter);
    } else {
      hotelGroupAFilter = getHotelGroupASchema([hotelFocusOn?.hotelCode], groupAFilter);
    }
    //
    let dimHotelIds = [];
    if (enableHotelGroups) {
      if (enableSecondaryHotelGroups) {
        dimHotelIds = new Set([...groupFocusOnIds, ...hotelListIds]);
      } else {
        dimHotelIds = new Set([hotelFocusOn?.hotelCode, ...hotelListIds]);
      }
    } else if (enableSecondaryHotelGroups) {
      dimHotelIds = new Set([...groupFocusOnIds, hotelId?.hotelCode]);
    } else {
      dimHotelIds = new Set([hotelFocusOn?.hotelCode, hotelId?.hotelCode]);
    }
    if ((enableSecondaryHotelGroups || enableHotelGroups) && dimHotelIds?.length === 0) {
      dimHotelIds = new Set(hotelDropdownList?.map((filter) => filter?.hotelCode));
    }
    //
    const secondaryHotelIdFilter = getHotelIdSchema(Array.from(dimHotelIds), visualFilters);
    const hotelFilters = [secondaryHotelIdFilter, hotelGroupAFilter, hotelGroup1Filter];
    //
    const alignmentAndDateComparisonFilter = getAlignmentAndDateComparisonSchema(
      visualFilters,
      alignment?.id,
      dateComparison
    );
    const pmsSyncFilter = getPmsSyncSchema(visualFilters, pmsSyncToggle, TAGS.SUMMARY);
    const paceHorizonFilter = getPaceHorizonSchema(visualFilters, paceHorizon);
    const paceVariableFilter = getPaceVariableSchema(
      visualFilters,
      paceVariable,
      TAGS.SUMMARY_PACE_VARIABLE
    );
    const isForecastFilter = getIsForecastSchema(visualFilters, summaryForecastFilter);
    const roomForecastFilter = getRoomForecastSchema(visualFilters, pmsSyncToggle);
    const revenueForecastFilter = getRevenueForecastSchema(visualFilters, pmsSyncToggle);
    const farFieldPmsFilter = getFarFieldPmsSyncSchema(visualFilters, pmsSyncToggle);
    const normalizedFilter = getNormalizedViewSchema(visualFilters, normalizedViewToggle);
    const searchPmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    const { adjustedStayDate } = calculateComparisonDates(
      targetDate,
      bookingDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      { startDate, endDate },
      resetBookingDate,
      partialHours,
      lastPartialBookingDate
    );
    //
    const comparisonStayDateFilter = getComparisonStayDateSchema(
      visualFilters,
      adjustedStayDate?.startDate,
      adjustedStayDate?.endDate
    );
    const maxStayDateFilter = getMaxStayDateSchema(visualFilters, targetDate?.endDate);
    //
    if (
      targetDateFilter &&
      paceHorizonFilter &&
      paceVariableFilter &&
      roomForecastFilter &&
      revenueForecastFilter &&
      alignmentAndDateComparisonFilter &&
      farFieldPmsFilter &&
      isForecastFilter &&
      normalizedFilter &&
      searchPmsSyncSchema
    ) {
      const filters = [
        ...pmsSyncFilter,
        ...searchPmsSyncSchema,
        targetDateFilter,
        paceHorizonFilter,
        paceVariableFilter,
        roomForecastFilter,
        revenueForecastFilter,
        ...alignmentAndDateComparisonFilter,
        ...farFieldPmsFilter,
        isForecastFilter,
        ...hotelFilters,
        normalizedFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];
      if (JSON.stringify(filters) !== JSON.stringify(paceVisualFilterList)) {
        dispatch(searchActions.setPaceVisualFilterList(filters));
      }
    }
  }, [
    pmsSyncToggle,
    visualFilters,
    dateComparison,
    alignment,
    targetDate,
    hotelId,
    paceHorizon,
    paceVariable,
    endDate,
    startDate,
    visualToggle,
    enableHotelGroups,
    summaryForecastFilter,
    reportType,
    enableSecondaryHotelGroups,
    hotelFocusOn,
    hotelGroupsDropdownList,
    normalizedViewToggle,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
    isSecondaryHotelSetOverTime,
    invalidSecondaryHotels,
  ]);

  useEffect(() => {
    if (
      (!dateComparison && !alignment && !bookingDate) ||
      (dateComparison && dateComparison[0] === KEYS.CUSTOM && endDateComparison === null)
    ) {
      return;
    }
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    const alignmentAndDateComparisonFilter = getTrendTimeAlignmentAndDateComparisonSchema(
      visualFilters,
      alignment?.id,
      dateComparison
    );
    const isForecastFilter = getIsForecastSchema(visualFilters, isTrendForecast);
    const targetDateFilter = getTargetDateSchema(visualFilters, startDate, endDate);
    const pmsSyncFilter = getPmsSyncToggleSchema(visualFilters, pmsSyncToggle);
    const symmetricComparisonFilter = getSymmetricComparisonSchema(
      visualFilters,
      symmetricComparisonToggle
    );
    const roomForecastFilter = getRoomForecastSchema(visualFilters, pmsSyncToggle);
    const revenueForecastFilter = getRevenueForecastSchema(visualFilters, pmsSyncToggle);
    const selectedBreakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
    const deAggregateSegmentAndFocusOnFilter = getTimeSegmentBreakdownAndFocusOnSchema(
      visualFilters,
      chartBreakdown,
      isDetailed,
      focusOn,
      hotelFocusOn?.id === hotelId?.id
    );
    const segmentFocusOnFilter = getSegmentFocusOnSchema(
      selectedBreakdown,
      focusOn?.map((item) => item?.label)
    );
    const granularityFilter = getGranularitySchema(visualFilters, granularity);
    const hotelGroupOrderFilter = getHotelGroupOrderSchema(visualFilters, isDemoUser);
    const movingAverageFilter = getMovingAverageSchema(visualFilters, movingAverage);
    const pmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    const normalizedFilter = getNormalizedViewSchema(visualFilters, normalizedViewToggle);
    //
    let hotelIdFilter = [];
    let hotelListIds = [];
    let hotelGroup1Filter;

    // Extract invalid primary IDs
    const invalidPrimaryIds = invalidPrimaryHotels.map((hotel) => hotel.hotelCode);

    if (hotelId?.hotels) {
      if (isPrimaryHotelSetOverTime) {
        hotelListIds = hotelId.hotels
          .filter((hotel) => !invalidPrimaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }

    // Extract invalid secondary IDs
    const invalidSecondaryIds = invalidSecondaryHotels.map((hotel) => hotel.hotelCode);

    let groupFocusOnIds = [];
    if (hotelFocusOn?.hotels) {
      if (isSecondaryHotelSetOverTime) {
        groupFocusOnIds = hotelFocusOn.hotels
          .filter((hotel) => !invalidSecondaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        groupFocusOnIds = hotelFocusOn?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }
    //
    if (enableHotelGroups) {
      hotelIdFilter = getHotelIdSchema(hotelListIds, visualFilters);
    } else {
      hotelIdFilter = getHotelIdSchema(hotelId?.hotelCode, visualFilters);
    }
    //
    const groupHotelFilter = getGroupHotelFilterSchema(visualFilters);
    if (enableHotelGroups) {
      hotelGroup1Filter = getHotelGroupSchema(hotelListIds, groupHotelFilter);
    } else {
      hotelGroup1Filter = getHotelGroupSchema([hotelId?.hotelCode], groupHotelFilter);
    }
    //
    const groupAFilter = getGroupASchema(visualFilters);
    let hotelGroupAFilter;
    if (enableSecondaryHotelGroups) {
      hotelGroupAFilter = getHotelGroupASchema(groupFocusOnIds, groupAFilter);
    } else {
      hotelGroupAFilter = getHotelGroupASchema([hotelFocusOn?.hotelCode], groupAFilter);
    }
    //
    let dimHotelIds = [];
    if (enableHotelGroups) {
      if (enableSecondaryHotelGroups) {
        dimHotelIds = new Set([...groupFocusOnIds, ...hotelListIds]);
      } else {
        dimHotelIds = new Set([hotelFocusOn?.hotelCode, ...hotelListIds]);
      }
    } else if (enableSecondaryHotelGroups) {
      dimHotelIds = new Set([...groupFocusOnIds, hotelId?.hotelCode]);
    } else {
      dimHotelIds = new Set([hotelFocusOn?.hotelCode, hotelId?.hotelCode]);
    }
    //
    const secondaryHotelIdFilter = getHotelIdSchema(Array.from(dimHotelIds), visualFilters);
    //
    const hotelFilters = [secondaryHotelIdFilter, hotelGroupAFilter, hotelGroup1Filter];
    //
    const { adjustedBookingDate, adjustedStayDate } = calculateComparisonDates(
      { startDate, endDate },
      bookingDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      {
        startDate: startDateComparison,
        endDate: endDateComparison,
      },
      resetBookingDate,
      partialHours,
      lastPartialBookingDate
    );
    //
    const comparisonBookingDateFilter = getComparisonBookingDateSchema(
      visualFilters,
      adjustedBookingDate?.startDate,
      adjustedBookingDate?.endDate
    );
    const comparisonStayDateFilter = getComparisonStayDateSchema(
      visualFilters,
      adjustedStayDate?.startDate,
      adjustedStayDate?.endDate
    );
    const maxStayDateFilter = getMaxStayDateSchema(visualFilters, endDate);
    //
    if (
      bookingDateFilter &&
      alignmentAndDateComparisonFilter &&
      targetDateFilter &&
      hotelIdFilter &&
      roomForecastFilter &&
      revenueForecastFilter &&
      symmetricComparisonFilter &&
      hotelGroupOrderFilter &&
      segmentFocusOnFilter
    ) {
      let filters = [
        ...pmsSyncFilter,
        bookingDateFilter,
        ...alignmentAndDateComparisonFilter,
        targetDateFilter,
        hotelIdFilter,
        symmetricComparisonFilter,
        roomForecastFilter,
        revenueForecastFilter,
        ...segmentFocusOnFilter,
        isForecastFilter,
        ...pmsSyncSchema,
        comparisonBookingDateFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];

      if (!isTrendPercentageChange) {
        filters = [...filters, ...deAggregateSegmentAndFocusOnFilter];
      }
      if (isMovingAverage) {
        filters = [...filters, movingAverageFilter];
      }
      // tabular filter list
      let tabularFilters = [
        ...pmsSyncFilter,
        bookingDateFilter,
        ...alignmentAndDateComparisonFilter,
        targetDateFilter,
        hotelIdFilter,
        symmetricComparisonFilter,
        roomForecastFilter,
        revenueForecastFilter,
        hotelGroupOrderFilter,
        movingAverageFilter,
        ...segmentFocusOnFilter,
        isForecastFilter,
        ...pmsSyncSchema,
        comparisonBookingDateFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];
      if (!isTrendPercentageChange) {
        tabularFilters = [...tabularFilters, ...deAggregateSegmentAndFocusOnFilter];
      }
      if (!weeklyViewToggle) {
        tabularFilters = [...tabularFilters, granularityFilter];
      }
      // dispatch filter list for trend time based on active tab

      if (JSON.stringify(filters) !== JSON.stringify(trendTimeFilterList)) {
        dispatch(searchActions.updateTrendTimeFilterList(filters));
      }
      if (JSON.stringify(tabularFilters) !== JSON.stringify(trendTabularFilterList)) {
        dispatch(searchActions.updateTrendTimeTabularFilterList(tabularFilters));
      }

      // dispatch filter list for time segment visuals based on active tab
      let hotelSegmentFilters = [
        ...pmsSyncFilter,
        bookingDateFilter,
        targetDateFilter,
        ...hotelFilters,
        ...segmentFocusOnFilter,
        ...pmsSyncSchema,
        normalizedFilter,
      ];
      // tabular filter list
      let hotelSegmentTabularFilters = [
        ...pmsSyncFilter,
        bookingDateFilter,
        targetDateFilter,
        ...hotelFilters,
        hotelGroupOrderFilter,
        movingAverageFilter,
        ...segmentFocusOnFilter,
        ...pmsSyncSchema,
        normalizedFilter,
      ];
      if (!isTrendPercentageChange) {
        hotelSegmentFilters = [...hotelSegmentFilters, ...deAggregateSegmentAndFocusOnFilter];
        hotelSegmentTabularFilters = [
          ...hotelSegmentTabularFilters,
          ...deAggregateSegmentAndFocusOnFilter,
        ];
      }
      if (isMovingAverage) {
        hotelSegmentFilters = [...hotelSegmentFilters, movingAverageFilter];
      }
      if (!weeklyViewToggle) {
        hotelSegmentTabularFilters = [...hotelSegmentTabularFilters, granularityFilter];
      }
      if (isTrendDeAggregate && hotelFocusOn?.id !== hotelId?.id) {
        dispatch(searchActions.updateTrendHotelSegmentFilterList([]));
        dispatch(searchActions.updateTrendHotelSegmentTabularFilterList([]));
        dispatch(searchActions.updateTrendHotelSegmentFilterList(hotelSegmentFilters));
        dispatch(
          searchActions.updateTrendHotelSegmentTabularFilterList(hotelSegmentTabularFilters)
        );
      }
    }
  }, [
    hotelFocusOn,
    bookingDate,
    visualFilters,
    dateComparison,
    alignment,
    targetDate,
    hotelId,
    pmsSyncToggle,
    weeklyViewToggle,
    symmetricComparisonToggle,
    isCumulative,
    startDateComparison,
    endDateComparison,
    isDetailed,
    focusOn,
    enableHotelGroups,
    isTrendForecast,
    reportType,
    isTrendPercentageChange,
    granularity,
    isTrendDeAggregate,
    isMovingAverage,
    movingAverage,
    normalizedViewToggle,
    enableSecondaryHotelGroups,
    startDate,
    endDate,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
    isSecondaryHotelSetOverTime,
    invalidSecondaryHotels,
    alignmentToggle,
    resetBookingDate,
  ]);
  //
  useEffect(() => {
    if (
      !focusOn?.length > 0 ||
      !hotelId ||
      (!chartBreakdown && !targetDate && !hotelId && !bookingDate)
    ) {
      return;
    }
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    const targetDateFilter = getTargetDateSchema(visualFilters, startDate, endDate);

    // Extract invalid primary IDs
    const invalidPrimaryIds = invalidPrimaryHotels.map((hotel) => hotel.hotelCode);

    let hotelIdFilter = [];
    if (enableHotelGroups) {
      let hotelListIds;
      if (isPrimaryHotelSetOverTime) {
        hotelListIds = hotelId.hotels
          .filter((hotel) => !invalidPrimaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
      hotelIdFilter = getHotelIdSchema(hotelListIds, visualFilters);
    } else {
      hotelIdFilter = getHotelIdSchema(hotelId?.hotelCode, visualFilters);
    }
    //
    const segmentAndFocusOnFilter = getTSBreakdownAndFocusOnSchema(
      visualFilters,
      chartBreakdown,
      isDetailed,
      focusOn,
      segmentGroup1,
      segmentGroup2
    );
    const pmsSyncFilter = getPmsSyncToggleSchema(visualFilters, pmsSyncToggle);
    const group1And2Filter = getTrendGroup1And2Schema(visualFilters, segmentGroup1, segmentGroup2);
    const granularityFilter = getGranularitySchema(visualFilters, granularity);
    const movingAverageFilter = getMovingAverageSchema(visualFilters, movingAverage);
    const pmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    //
    if (
      bookingDateFilter &&
      targetDateFilter &&
      hotelIdFilter &&
      segmentAndFocusOnFilter &&
      group1And2Filter
    ) {
      const filters = [
        ...pmsSyncFilter,
        bookingDateFilter,
        targetDateFilter,
        hotelIdFilter,
        ...segmentAndFocusOnFilter,
        ...group1And2Filter,
        movingAverageFilter,
        ...pmsSyncSchema,
      ];
      dispatch(searchActions.updateTrendSegmentFilterList(filters));

      // tabular filter list
      let tabularFilters = [
        ...pmsSyncFilter,
        bookingDateFilter,
        targetDateFilter,
        hotelIdFilter,
        ...segmentAndFocusOnFilter,
        ...group1And2Filter,
        movingAverageFilter,
        ...pmsSyncSchema,
      ];
      if (!weeklyViewToggle) {
        tabularFilters = [...tabularFilters, granularityFilter];
      }
      dispatch(searchActions.updateTrendSegmentTabularFilterList(tabularFilters));
    }
  }, [
    isDetailed,
    focusOn,
    chartBreakdown,
    bookingDate,
    visualFilters,
    targetDate,
    hotelId,
    pmsSyncToggle,
    weeklyViewToggle,
    targetStartDate,
    targetEndDate,
    segmentGroup1,
    segmentGroup2,
    isCumulative,
    enableHotelGroups,
    reportType,
    granularity,
    adjustedStartDate,
    adjustedEndDate,
    isMovingAverage,
    movingAverage,
    startDate,
    endDate,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
  ]);

  // Triggered when targetDate value changes
  useEffect(() => {
    // Set stay dates based on granularity
    const trendGranularity = getGranularity(targetDate);
    dispatch(searchActions.setGranularity(trendGranularity));
    if (trendGranularity === GRANULARITY.WEEKLY) {
      if (isSunday(new Date(targetDate.startDate))) {
        dispatch(searchActions.setAdjustedStartDate(new Date(targetDate.startDate).toISOString()));
      } else {
        dispatch(
          searchActions.setAdjustedStartDate(
            previousSunday(new Date(targetDate.startDate)).toISOString()
          )
        );
      }
      if (isSaturday(new Date(targetDate.endDate))) {
        dispatch(searchActions.setAdjustedEndDate(new Date(targetDate.endDate).toISOString()));
      } else {
        dispatch(
          searchActions.setAdjustedEndDate(nextSaturday(new Date(targetDate.endDate)).toISOString())
        );
      }
    } else if (trendGranularity === GRANULARITY.MONTHLY) {
      if (isFirstDayOfMonth(new Date(targetDate.startDate))) {
        dispatch(searchActions.setAdjustedStartDate(new Date(targetDate.startDate).toISOString()));
      } else {
        dispatch(
          searchActions.setAdjustedStartDate(
            startOfMonth(new Date(targetDate.startDate)).toISOString()
          )
        );
      }
      if (isLastDayOfMonth(new Date(targetDate.endDate))) {
        dispatch(searchActions.setAdjustedEndDate(new Date(targetDate.endDate).toISOString()));
      } else {
        dispatch(
          searchActions.setAdjustedEndDate(endOfMonth(new Date(targetDate.endDate)).toISOString())
        );
      }
    } else if (trendGranularity === GRANULARITY.YEARLY) {
      if (isSameDay(startOfYear(new Date(targetDate.startDate)), new Date(targetDate.startDate))) {
        dispatch(searchActions.setAdjustedStartDate(new Date(targetDate.startDate).toISOString()));
      } else {
        dispatch(
          searchActions.setAdjustedStartDate(
            startOfYear(new Date(targetDate.startDate)).toISOString()
          )
        );
      }
      if (isSameDay(lastDayOfYear(new Date(targetDate.endDate)), new Date(targetDate.endDate))) {
        dispatch(searchActions.setAdjustedEndDate(new Date(targetDate.endDate).toISOString()));
      } else {
        dispatch(
          searchActions.setAdjustedEndDate(
            lastDayOfYear(new Date(targetDate.endDate)).toISOString()
          )
        );
      }
    }
  }, [targetDate]);
};
//
export default useSearchFilter;
